import { createSelector, createStructuredSelector } from 'reselect';
import {
  constant,
  compact,
  flow,
  get,
  identity,
  isEmpty,
  pickBy,
} from 'lodash/fp';
import { getGlobalPromoCmsResult } from 'helpers/globalPromo';
import { isSecondaryPromoBannerFeature } from 'modules/features';
import { AD_TYPE_PROMO } from '../../constants';
import {
  DEFAULT_PROMO_VARIANT,
  SECONDARY_PROMO_VARIANT,
} from '../content/SearchContent/AdList/constants';

const ADS_BEFORE_PROMO_FORM = 2;
const ADS_BEFORE_SECONDARY_PROMO_FORM = 6;

const preparePromo = createStructuredSelector({
  content: get(['promo-slot-content']),
  imageSource: get(['promo-slot-image-source']),
  linkSource: get(['promo-slot-link-source']),
  linkTitle: get(['promo-slot-link-title']),
  title: get(['promo-slot-title']),
});

const prepareSecondaryPromo = createStructuredSelector({
  content: get(['secondary-promo-slot-content']),
  contentMobile: get(['secondary-promo-slot-content-mobile']),
  imageSource: get(['secondary-promo-slot-image-source']),
  linkSource: get(['secondary-promo-slot-link-source']),
  linkTitle: get(['secondary-promo-slot-link-title']),
  linkTitleMobile: get(['secondary-promo-slot-link-title-mobile']),
  title: get(['secondary-promo-slot-title']),
});

const getPromoAd = (getCms = identity) =>
  createSelector(
    getCms,
    flow(getGlobalPromoCmsResult, preparePromo),
    (pageCmsPromo, globalPromo) => {
      let promo = preparePromo(pageCmsPromo);

      if (flow(pickBy(identity), isEmpty)(promo)) {
        promo = globalPromo;
      }

      return {
        ...promo,
        id: 0,
        type: AD_TYPE_PROMO,
        variant: DEFAULT_PROMO_VARIANT,
      };
    },
  );

const getSecondaryPromoAd = (getCms = identity) =>
  createSelector(
    getCms,
    flow(getGlobalPromoCmsResult, prepareSecondaryPromo),
    (pageCmsPromo, globalPromo) => {
      let promo = prepareSecondaryPromo(pageCmsPromo);

      if (flow(pickBy(identity), isEmpty)(promo)) {
        promo = globalPromo;
      }

      return {
        ...promo,
        id: -1,
        type: AD_TYPE_PROMO,
        variant: SECONDARY_PROMO_VARIANT,
      };
    },
  );

const getTrue = constant(true);

export default (getItems, getCms, withPromoBanner = getTrue) =>
  createSelector(
    getItems,
    getPromoAd(getCms),
    getSecondaryPromoAd(getCms),
    withPromoBanner,
    (items, promoAd, secondaryPromoAd, withPromoBanner) =>
      flow((items) => {
        if (!withPromoBanner || !promoAd.linkSource) {
          return items;
        }

        return compact([
          ...items.slice(0, ADS_BEFORE_PROMO_FORM),
          items.length > ADS_BEFORE_PROMO_FORM ? promoAd : null,
          ...items.slice(
            ADS_BEFORE_PROMO_FORM,
            ADS_BEFORE_SECONDARY_PROMO_FORM,
          ),
          isSecondaryPromoBannerFeature &&
          items.length > ADS_BEFORE_SECONDARY_PROMO_FORM
            ? secondaryPromoAd
            : null,
          ...items.slice(ADS_BEFORE_SECONDARY_PROMO_FORM, items.length),
        ]);
      })(items),
  );
